import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SUPPORTED_SEO_LANGS, SeoLang } from "@/lib/seo-config";
import { getDomainConfig, urlFor, originFor } from "@/lib/domain-config";

const STORAGE_KEY = "td_lang_pref"; // set when user manually picks a language
const REDIRECT_KEY = "td_lang_redirected"; // set after auto-redirect to prevent loops

// Heuristic bot detection — keep crawlers on the canonical Danish "/" so
// Google's hreflang signals work correctly.
function isLikelyBot(ua: string): boolean {
  return /bot|crawl|spider|slurp|bingpreview|facebookexternalhit|linkedinbot|whatsapp|telegrambot|preview|lighthouse|headlesschrome/i.test(
    ua
  );
}

function detectBrowserLang(): SeoLang | null {
  try {
    const raw = (navigator.language || "").toLowerCase();
    const short = raw.split("-")[0];
    // pt-BR / pt-PT → pt
    if (short === "pt") return "pt";
    // nn (nynorsk) → nb
    if (short === "nn") return "nb";
    if ((SUPPORTED_SEO_LANGS as string[]).includes(short)) return short as SeoLang;
  } catch {}
  return null;
}

/**
 * On a multi-ccTLD setup, the hostname already encodes the language for
 * relark.dk/no/se/de — so no redirect is needed there. We only auto-redirect
 * when the visitor is on a *non-canonical* origin (relark.app, lovable.app
 * preview, localhost) AND their browser language matches a different ccTLD.
 *
 * Behaviour:
 * - On relark.dk/no/se/de  → never redirect (hostname == locale).
 * - On relark.app          → if browser is da/nb/sv/de, hard-redirect to the
 *                            matching ccTLD; otherwise stay (for en/es/fr/pt).
 * - On dev / preview       → never redirect (would break local testing).
 */
export function useBrowserLanguageRedirect() {
  const location = useLocation();

  useEffect(() => {
    // Only act on the bare root path
    if (location.pathname !== "/") return;

    // Don't redirect bots — they need to see the canonical Danish version
    if (typeof navigator !== "undefined" && isLikelyBot(navigator.userAgent)) return;

    // Respect manual language choice (set via the language switcher)
    if (localStorage.getItem(STORAGE_KEY)) return;

    // Don't loop — only auto-redirect once per browser
    if (sessionStorage.getItem(REDIRECT_KEY)) return;

    const domain = getDomainConfig();
    // ccTLDs are already canonical for their language → never redirect.
    if (domain.role === "ccTLD") return;
    // Dev / preview → never redirect, would break local testing.
    if (domain.role === "dev") return;
    // Trust subdomain has its own dedicated content.
    if (domain.role === "trust") return;

    const lang = detectBrowserLang();
    if (!lang) return;

    // On relark.app: if user's language has its own ccTLD, send them there.
    const targetOrigin = originFor(lang);
    const currentOrigin = `https://${domain.hostname}`;
    if (targetOrigin === currentOrigin) {
      // Same domain (e.g. en on relark.app) → no cross-domain redirect needed.
      // Path-prefix languages (es/fr/pt) are handled by router-level routing.
      return;
    }

    sessionStorage.setItem(REDIRECT_KEY, "1");
    // Tell the banner which language we redirected the user into.
    sessionStorage.setItem("td_lang_redirect_notice", lang);
    window.location.href = urlFor(lang);
  }, [location.pathname]);
}

/** Call from the language switcher to mark the choice as deliberate. */
export function markLanguagePreference(lang: string) {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {}
}
